import React, { useState, useMemo, useEffect } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VideocamIcon from "@mui/icons-material/Videocam";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import { submitSessionRequest, fetchTenantLocations } from "../api/apiClient";
import RoomPicker from "./RoomPicker";
import { useTenant } from "../contexts/TenantContext";

const T = {
  surface2: "var(--color-surface-2)",
  line:     "#E4E7EB",
  line2:    "var(--color-hairline-cloud)",
  surface3: "var(--color-surface-2)",
  ink:      "var(--color-ink)",
  ink2:     "var(--color-ink-2)",
  ink3:     "var(--color-ink-3)",
  ink4:     "var(--color-ink-4)",
  danger:   "#B42318",
  live:     "#C2410C",
  live50:   "rgba(194,65,12,.10)",
};

function makeCtrlSx(tPrimary) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "11px",
      bgcolor: "#fff",
      "& fieldset": { borderWidth: "1.5px", borderColor: T.line },
      "&:hover fieldset": { borderColor: "var(--color-hairline-cool)" },
      "&.Mui-focused fieldset": { borderColor: tPrimary, borderWidth: "1.5px", boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.09)}` },
    },
    "& .MuiInputBase-input": { py: "9px", fontSize: "13.5px", fontWeight: 500, color: T.ink },
    "& .MuiInputAdornment-root": { color: T.ink4 },
  };
}

function FieldLabel({ children, required }) {
  return (
    <Typography sx={{
      display: "block", fontSize: "10.5px", fontWeight: 800, letterSpacing: ".08em",
      textTransform: "uppercase", color: T.ink3, mb: "6px",
    }}>
      {children}
      {required && <Box component="span" sx={{ color: T.danger, ml: "2px" }}>*</Box>}
    </Typography>
  );
}

function CatIcon({ cat, size = 22 }) {
  const shapes = {
    ff:  () => <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
    ems: () => <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>,
    hm:  () => <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    fo:  () => <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    gen: () => <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
  };
  const Content = shapes[cat] || shapes.gen;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <Content />
    </svg>
  );
}

function getCatKey(category) {
  if (!category) return "gen";
  const c = category.toLowerCase();
  if (c.includes("officer")) return "fo";
  if (c.includes("hazmat") || c.includes("hazardous")) return "hm";
  if (c.includes("ems") || c.includes("medical")) return "ems";
  if (c.includes("fire")) return "ff";
  return "gen";
}

function makeCatColors(tPrimary, tPrimary10, tAccent) {
  return {
    bg: { ff: T.live50, ems: "rgba(37,99,235,.10)", hm: alpha(tAccent, 0.10), fo: tPrimary10, gen: T.surface3 },
    fg: { ff: T.live,   ems: "var(--color-primary)",              hm: tAccent,              fo: tPrimary,   gen: T.ink3 },
  };
}

const EMPTY_SESSION = {
  date: "",
  startTime: "08:00",
  endTime: "12:00",
  sessionType: "PHYSICAL",
  location: "",
  meetingLink: "",
  capacity: 30,
  roomId: null,
};

export default function ScheduleSessionsDialog({ open, onClose, onSuccess, proposal }) {
  const theme = useTheme();
  const tPrimary   = theme.palette.primary.main;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tSecondary = theme.palette.secondary.main;
  const tSecondary50 = alpha(tSecondary, 0.10);
  const tAccent    = theme.palette.warning.main;
  const CTRL_SX = makeCtrlSx(tPrimary);
  const { bg: CAT_BG, fg: CAT_FG } = makeCatColors(tPrimary, tPrimary10, tAccent);
  const { user } = useTenant();
  const [sessions, setSessions] = useState([{ ...EMPTY_SESSION, _id: 1 }]);
  const [nextId, setNextId]     = useState(2);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState(false);
  const [locations, setLocations] = useState([]);

  const totalHours = proposal?.totalHours || 0;
  const classSeats = proposal?.maxStudents || proposal?.availableSeats || 0;

  useEffect(() => {
    if (!open) return;
    const defaultCapacity = classSeats > 0 ? classSeats : 30;
    setSessions([{ ...EMPTY_SESSION, capacity: defaultCapacity, _id: 1 }]);
    setNextId(2);
    setError("");
    setSuccess(false);
    fetchTenantLocations().then((data) => {
      setLocations(Array.isArray(data) ? data : []);
    }).catch(() => setLocations([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scheduledHours = useMemo(() => {
    let total = 0;
    sessions.forEach((s) => {
      if (!s.startTime || !s.endTime) return;
      const [sh, sm] = s.startTime.split(":").map(Number);
      const [eh, em] = s.endTime.split(":").map(Number);
      const diff = (eh * 60 + em) - (sh * 60 + sm);
      if (diff > 0) total += diff / 60;
    });
    return total;
  }, [sessions]);

  const hoursPct = totalHours > 0 ? Math.min(100, (scheduledHours / totalHours) * 100) : 0;
  const hoursOver = scheduledHours > totalHours;

  function handleClose() {
    setSessions([{ ...EMPTY_SESSION, _id: 1 }]);
    setNextId(2);
    setError("");
    setSuccess(false);
    onClose();
  }

  function addSession() {
    const defaultCapacity = classSeats > 0 ? classSeats : 30;
    setSessions((prev) => [...prev, { ...EMPTY_SESSION, capacity: defaultCapacity, _id: nextId }]);
    setNextId((id) => id + 1);
  }

  function removeSession(_id) {
    setSessions((prev) => {
      const filtered = prev.filter((s) => s._id !== _id);
      return filtered.length ? filtered : [{ ...EMPTY_SESSION, _id: nextId }];
    });
    if (sessions.length <= 1) setNextId((id) => id + 1);
  }

  function updateSession(_id, updates) {
    setSessions((prev) => prev.map((s) => (s._id === _id ? { ...s, ...updates } : s)));
  }

  async function handleSubmit() {
    setError("");
    // validation
    for (const s of sessions) {
      if (!s.date) { setError("Each session requires a date."); return; }
      if (!s.startTime || !s.endTime) { setError("Each session requires start and end times."); return; }
      if (s.sessionType === "PHYSICAL" && !s.location) { setError("Each in-person session requires a location."); return; }
      if (s.sessionType === "VIRTUAL" && !s.meetingLink.trim()) { setError("Each virtual session requires a meeting link."); return; }
    }
    if (!proposal?.courseId) { setError("No course linked to this proposal."); return; }
    if (!user?.id) { setError("Could not identify instructor. Please refresh and try again."); return; }

    setSaving(true);
    try {
      await Promise.all(
        sessions.map((s) =>
          submitSessionRequest(proposal.courseId, {
            instructorId: user.id,
            sessionDate: s.date,
            startTime: s.startTime,
            endTime: s.endTime,
            sessionType: s.sessionType,
            location: s.sessionType === "PHYSICAL" ? s.location : undefined,
            meetingLink: s.sessionType === "VIRTUAL" ? s.meetingLink : undefined,
            capacity: s.capacity,
            roomId: s.roomId ?? undefined,
          })
        )
      );
      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to create sessions.");
    } finally {
      setSaving(false);
    }
  }

  const catKey = getCatKey(proposal?.category);
  const catBg = CAT_BG[catKey];
  const catFg = CAT_FG[catKey];

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: "100%", maxWidth: 640, borderRadius: "22px",
          boxShadow: "0 32px 80px -20px rgba(20,24,40,.4)",
          overflow: "hidden", m: 2, display: "flex", flexDirection: "column", maxHeight: "90vh",
        },
      }}
    >
      {/* Banner */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "14px",
        p: "18px 22px", borderBottom: `1px solid ${T.line2}`,
        bgcolor: T.surface2, flexShrink: 0, position: "relative",
      }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: "13px",
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: catBg, color: catFg, flexShrink: 0,
        }}>
          <CatIcon cat={catKey} size={20} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 16, fontWeight: 800, color: T.ink,
            letterSpacing: "-.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            fontFamily: "var(--font-display)",
          }}>
            {proposal?.title || "Schedule Sessions"}
          </Typography>
          <Box sx={{ display: "flex", gap: "14px", mt: "4px", flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.ink3, display: "flex", alignItems: "center", gap: "5px" }}>
              <AccessTimeIcon sx={{ fontSize: 12, color: T.ink4 }} />
              {totalHours} hrs total
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.ink3, display: "flex", alignItems: "center", gap: "5px" }}>
              <CalendarTodayIcon sx={{ fontSize: 12, color: T.ink4 }} />
              {proposal?.category || "Course"}
            </Typography>
            <Typography sx={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: T.ink3 }}>
              #{proposal?.id}
            </Typography>
          </Box>
        </Box>
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: "5px",
          fontSize: 11, fontWeight: 700, px: "10px", py: "4px",
          borderRadius: "999px", border: `1.5px solid ${tSecondary50}`,
          bgcolor: tSecondary50, color: tSecondary, flexShrink: 0,
        }}>
          <CheckCircleIcon sx={{ fontSize: 11 }} />
          Approved
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={saving}
          size="small"
          sx={{
            position: "absolute", top: 14, right: 14,
            width: 30, height: 30, borderRadius: "8px",
            bgcolor: T.surface3, color: T.ink3,
            "&:hover": { bgcolor: "rgba(180,35,24,.08)", color: T.danger },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {success ? (
        <Box sx={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: "18px", p: "64px 32px", textAlign: "center",
        }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: "50%", bgcolor: tSecondary50,
            display: "flex", alignItems: "center", justifyContent: "center", color: tSecondary,
          }}>
            <CheckCircleIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: "-.025em", color: T.ink, mb: "8px", fontFamily: "var(--font-display)" }}>
              Sessions Submitted!
            </Typography>
            <Typography sx={{ fontSize: 14, color: T.ink3, maxWidth: "30ch", lineHeight: 1.6, mx: "auto" }}>
              Your session requests are pending coordinator approval. You'll be notified once they're reviewed.
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            sx={{
              height: 44, px: "24px", borderRadius: "12px",
              bgcolor: tSecondary, color: "#fff", fontSize: 14, fontWeight: 700,
              textTransform: "none", "&:hover": { bgcolor: "var(--color-success)" },
            }}
          >
            Done
          </Button>
        </Box>
      ) : (
        <>
          {/* Hours bar */}
          <Box sx={{
            display: "flex", flexDirection: "column", gap: "7px",
            bgcolor: T.surface2, border: `1.5px solid ${T.line2}`,
            borderRadius: "12px", p: "12px 16px", flexShrink: 0, m: "16px 22px 0",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: T.ink3 }}>
                Hours scheduled
              </Typography>
              <Typography sx={{
                fontSize: 13, fontWeight: 800, color: hoursOver ? T.danger : T.ink,
              }}>
                {scheduledHours % 1 === 0 ? scheduledHours : scheduledHours.toFixed(1)} / {totalHours} hrs
              </Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: "999px", bgcolor: T.line2, overflow: "hidden" }}>
              <Box sx={{
                height: "100%", borderRadius: "999px", bgcolor: hoursOver ? T.danger : tSecondary,
                transition: "width .3s, background .3s", width: `${hoursPct}%`,
              }} />
            </Box>
          </Box>

          {/* Session list header */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: "12px", flexShrink: 0, px: "22px", pt: "16px", pb: "6px",
          }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase",
              color: T.ink4, display: "flex", alignItems: "center", gap: "8px",
            }}>
              Sessions
              <Box component="span" sx={{
                fontSize: 11, fontWeight: 700, px: "8px", py: "2px", borderRadius: "999px",
                bgcolor: tPrimary10, color: tPrimary,
              }}>
                {sessions.length}
              </Box>
            </Typography>
            <Button
              onClick={addSession}
              startIcon={<AddIcon sx={{ fontSize: 13 }} />}
              disabled={saving}
              sx={{
                height: 32, px: "13px", borderRadius: "9px",
                border: `1.5px solid ${T.line}`, bgcolor: "#fff",
                fontSize: "12.5px", fontWeight: 700, color: tPrimary,
                textTransform: "none",
                "&:hover": { borderColor: tPrimary, bgcolor: tPrimary10 },
              }}
            >
              Add Session
            </Button>
          </Box>

          {/* Scrollable session list */}
          <Box sx={{ px: "22px", py: "8px", overflowY: "auto", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
            {error && <Alert severity="error" sx={{ borderRadius: "10px" }}>{error}</Alert>}

            {sessions.map((s, idx) => (
              <Box key={s._id} sx={{
                bgcolor: T.surface2, border: `1.5px solid ${T.line}`,
                borderRadius: "16px", overflow: "hidden",
                transition: "border-color .14s, box-shadow .14s",
                "&:focus-within": { borderColor: tPrimary, boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.08)}` },
              }}>
                {/* Session head */}
                <Box sx={{
                  display: "flex", alignItems: "center", gap: "10px",
                  p: "12px 16px 10px", borderBottom: `1px solid ${T.line2}`,
                }}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: "7px",
                    bgcolor: tPrimary, color: "#fff", fontSize: 11, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    {idx + 1}
                  </Box>
                  <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: T.ink, flex: 1, fontFamily: "var(--font-display)" }}>
                    Session {idx + 1}
                  </Typography>
                  <IconButton
                    onClick={() => removeSession(s._id)}
                    disabled={saving}
                    size="small"
                    sx={{
                      width: 26, height: 26, borderRadius: "7px",
                      bgcolor: "transparent", color: T.ink4,
                      "&:hover": { bgcolor: "rgba(180,35,24,.08)", color: T.danger },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>

                {/* Session body */}
                <Box sx={{ p: "14px 16px", display: "flex", flexDirection: "column", gap: "13px" }}>
                  {/* Date / Start / End */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                    <Box>
                      <FieldLabel required>Date</FieldLabel>
                      <TextField
                        fullWidth
                        type="date"
                        value={s.date}
                        onChange={(e) => updateSession(s._id, { date: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarTodayIcon sx={{ fontSize: 13, color: T.ink4 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={CTRL_SX}
                      />
                    </Box>
                    <Box>
                      <FieldLabel required>Start</FieldLabel>
                      <TextField
                        fullWidth
                        type="time"
                        value={s.startTime}
                        onChange={(e) => updateSession(s._id, { startTime: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccessTimeIcon sx={{ fontSize: 13, color: T.ink4 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={CTRL_SX}
                      />
                    </Box>
                    <Box>
                      <FieldLabel required>End</FieldLabel>
                      <TextField
                        fullWidth
                        type="time"
                        value={s.endTime}
                        onChange={(e) => updateSession(s._id, { endTime: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccessTimeIcon sx={{ fontSize: 13, color: T.ink4 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={CTRL_SX}
                      />
                    </Box>
                  </Box>

                  {/* Session Type */}
                  <Box>
                    <FieldLabel required>Session Type</FieldLabel>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                      <TypeOption
                        selected={s.sessionType === "PHYSICAL"}
                        onClick={() => updateSession(s._id, { sessionType: "PHYSICAL", meetingLink: "" })}
                        icon={<LocationOnIcon sx={{ fontSize: 16 }} />}
                        label="In-Person"
                        sub="Physical location"
                        tPrimary={tPrimary}
                        tPrimary10={tPrimary10}
                      />
                      <TypeOption
                        selected={s.sessionType === "VIRTUAL"}
                        onClick={() => updateSession(s._id, { sessionType: "VIRTUAL", location: "" })}
                        icon={<VideocamIcon sx={{ fontSize: 16 }} />}
                        label="Virtual"
                        sub="Online link"
                        tPrimary={tPrimary}
                        tPrimary10={tPrimary10}
                      />
                    </Box>
                  </Box>

                  {/* Location (in-person) */}
                  {s.sessionType === "PHYSICAL" && (
                    <Box>
                      <FieldLabel required>Location</FieldLabel>
                      <Select
                        fullWidth
                        displayEmpty
                        value={s.location}
                        onChange={(e) => updateSession(s._id, { location: e.target.value })}
                        sx={{
                          height: 40, borderRadius: "11px", bgcolor: "#fff",
                          fontSize: "13.5px", fontWeight: s.location ? 500 : 400,
                          color: s.location ? T.ink : T.ink4,
                          "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: T.line },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-hairline-cool)" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tPrimary, borderWidth: "1.5px" },
                        }}
                      >
                        <MenuItem value="" disabled sx={{ color: T.ink4 }}>Select a location</MenuItem>
                        {locations.map((loc) => (
                          <MenuItem key={loc.id || loc.name} value={loc.name || loc}>{loc.name || loc}</MenuItem>
                        ))}
                        <MenuItem value="Other">Other (specify in notes)</MenuItem>
                      </Select>
                    </Box>
                  )}

                  {/* Meeting link (virtual) */}
                  {s.sessionType === "VIRTUAL" && (
                    <Box>
                      <FieldLabel required>Meeting Link</FieldLabel>
                      <TextField
                        fullWidth
                        type="url"
                        value={s.meetingLink}
                        onChange={(e) => updateSession(s._id, { meetingLink: e.target.value })}
                        placeholder="https://zoom.us/j/…"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkIcon sx={{ fontSize: 13, color: T.ink4 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={CTRL_SX}
                      />
                    </Box>
                  )}

                  {/* Capacity */}
                  <Box>
                    <FieldLabel required>Capacity</FieldLabel>
                    <Box sx={{
                      display: "flex", alignItems: "center",
                      border: `1.5px solid ${T.line}`, borderRadius: "11px", overflow: "hidden", bgcolor: "#fff",
                    }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => updateSession(s._id, { capacity: Math.max(1, s.capacity - 5) })}
                        sx={{
                          width: 40, height: 40, border: 0, bgcolor: "transparent",
                          color: T.ink3, fontSize: 18, fontWeight: 400, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0, transition: "all .13s", fontFamily: "inherit",
                          "&:hover": { bgcolor: T.surface3, color: T.ink },
                        }}
                      >
                        −
                      </Box>
                      <Box sx={{ width: "1px", bgcolor: T.line2, height: 22 }} />
                      <Box
                        component="input"
                        type="number"
                        value={s.capacity}
                        onChange={(e) => updateSession(s._id, { capacity: Math.max(1, Math.min(classSeats > 0 ? classSeats : 9999, parseInt(e.target.value, 10) || 1)) })}
                        sx={{
                          flex: 1, textAlign: "center", fontSize: 15, fontWeight: 800,
                          color: T.ink, letterSpacing: "-.02em", border: 0, bgcolor: "transparent",
                          outline: "none", fontFamily: "inherit", height: 40,
                          "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none" },
                        }}
                      />
                      <Box sx={{ width: "1px", bgcolor: T.line2, height: 22 }} />
                      <Box
                        component="button"
                        type="button"
                        onClick={() => updateSession(s._id, { capacity: Math.min(classSeats > 0 ? classSeats : 200, s.capacity + 5) })}
                        sx={{
                          width: 40, height: 40, border: 0, bgcolor: "transparent",
                          color: T.ink3, fontSize: 18, fontWeight: 400, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0, transition: "all .13s", fontFamily: "inherit",
                          "&:hover": { bgcolor: T.surface3, color: T.ink },
                        }}
                      >
                        +
                      </Box>
                    </Box>
                  </Box>

                  {/* Room */}
                  <Box>
                    <FieldLabel>Room <Box component="span" sx={{ fontWeight: 400, color: T.ink4, textTransform: "none", letterSpacing: 0 }}>(optional)</Box></FieldLabel>
                    <RoomPicker
                      value={s.roomId}
                      onChange={(roomId) => updateSession(s._id, { roomId })}
                      date={s.date}
                      startTime={s.startTime}
                      endTime={s.endTime}
                      disabled={saving}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Footer */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "9px",
            p: "14px 22px 18px", borderTop: `1px solid ${T.line2}`,
            bgcolor: T.surface2, flexShrink: 0,
          }}>
            <Button
              onClick={handleClose}
              disabled={saving}
              sx={{
                height: 44, px: "20px", borderRadius: "12px",
                border: `1.5px solid ${T.line}`, bgcolor: "#fff",
                fontSize: 14, fontWeight: 600, color: T.ink2, textTransform: "none",
                "&:hover": { bgcolor: T.surface3, color: T.ink },
              }}
            >
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={handleSubmit}
              disabled={saving}
              startIcon={saving
                ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                : <SendIcon sx={{ fontSize: 15 }} />
              }
              sx={{
                height: 44, px: "22px", borderRadius: "12px",
                bgcolor: tSecondary, color: "#fff",
                fontSize: 14, fontWeight: 700, textTransform: "none",
                boxShadow: `0 6px 18px -6px ${alpha(tSecondary, 0.5)}`,
                "&:hover": { bgcolor: "var(--color-success)" },
                "&:active": { transform: "translateY(1px)" },
              }}
            >
              {saving ? "Submitting…" : "Submit for Approval"}
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
}

function TypeOption({ selected, onClick, icon, label, sub, tPrimary, tPrimary10 }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex", alignItems: "center", gap: "9px",
        p: "10px 13px", borderRadius: "11px", border: `1.5px solid ${selected ? tPrimary : T.line}`,
        bgcolor: selected ? tPrimary10 : "#fff", cursor: "pointer", transition: "all .14s", position: "relative",
        "&:hover": { borderColor: selected ? tPrimary : "var(--color-hairline-cool)" },
      }}
    >
      <Box sx={{
        width: 32, height: 32, borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: selected ? tPrimary : T.surface3, color: selected ? "#fff" : T.ink3, flexShrink: 0, transition: "all .14s",
      }}>
        {icon}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: selected ? tPrimary : T.ink2, transition: "color .14s" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 11, color: T.ink4, mt: "1px" }}>
          {sub}
        </Typography>
      </Box>
    </Box>
  );
}
